import { TextField as MUITextField, Box, TextFieldProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { gray } from "configs/colors";
import { rem } from "utils";

import { generateFormFieldStyles } from "../helpers";

export const Container = styled(Box)(() => ({
  display: "block",

  // Input Label
  "& > .MuiFormLabel-root": {
    marginBottom: rem(6),
  },

  // Helper text
  ".MuiFormHelperText-root": {
    marginLeft: 0,
    marginRight: 0,
    marginTop: rem(6),
    "&, &.Mui-disabled": {
      color: gray[600],
    },
  },
}));

export const InputWrapper = styled(Box)<{
  color: TextFieldProps["color"];
  fullWidth: boolean;
}>(({ theme, color, fullWidth }) => {
  const styles = generateFormFieldStyles({ theme, color });

  return {
    display: fullWidth ? "flex" : "inline-flex",
    boxShadow: styles.normal.boxShadow,
    borderRadius: theme.shape.borderRadius,
  };
});

export const AddonWrapper = styled(Box)<{
  placement: "before" | "after";
  disabled?: boolean;
}>(({ theme, placement, disabled }) => {
  const isBefore = placement === "before";
  const isAfter = placement === "after";
  const styles = generateFormFieldStyles({ theme });

  return {
    flex: "0 0 auto",
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    padding: `${rem(8)} ${rem(12)}`,
    fontSize: rem(14),
    height: rem(36),
    color: gray[600],
    backgroundColor: disabled
      ? styles.disabled.backgroundColor
      : styles.normal.backgroundColor,
    border: `1px solid ${
      disabled ? styles.disabled.borderColor : styles.normal.borderColor
    }`,
    borderTopLeftRadius: isBefore ? theme.shape.borderRadius : 0,
    borderBottomLeftRadius: isBefore ? theme.shape.borderRadius : 0,
    borderTopRightRadius: isAfter ? theme.shape.borderRadius : 0,
    borderBottomRightRadius: isAfter ? theme.shape.borderRadius : 0,
    ...(isBefore ? { borderRight: 0 } : {}),
    ...(isAfter ? { borderLeft: 0 } : {}),
    ...(disabled ? { pointerEvents: "none" } : {}),
  };
});

export const Input = styled(MUITextField)<{
  withAddonBefore?: boolean;
  withAddonAfter?: boolean;
}>(
  ({
    theme,
    color,
    withAddonAfter,
    withAddonBefore,
    multiline,
    InputProps,
  }) => {
    const styles = generateFormFieldStyles({
      theme,
      color,
    });

    return {
      marginTop: 0,

      // Input root
      ".MuiInputBase-root": {
        backgroundColor: styles.normal.backgroundColor,
        ...(withAddonBefore
          ? {
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }
          : {}),
        ...(withAddonAfter
          ? {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            }
          : {}),
        ...(multiline ? { padding: 0 } : {}), // For "Multiline" case
        "&.MuiOutlinedInput-root": {
          ...(InputProps?.startAdornment ? { paddingLeft: rem(12) } : {}),
          ...(InputProps?.endAdornment ? { paddingRight: rem(12) } : {}),
        },
      },

      // Input element
      ".MuiInputBase-input": {
        padding: `${rem(8)} ${rem(12)}`,
        height: rem(20),
      },

      // Fieldset
      ".MuiOutlinedInput-notchedOutline": {
        borderWidth: "1px !important",
        borderColor: styles.normal.borderColor,
        transition: theme.transitions.create([
          "border-color",
          "background-color",
          "box-shadow",
        ]),
      },

      // Disable
      ".Mui-disabled": {
        "&.MuiInputBase-root": {
          backgroundColor: styles.disabled.backgroundColor,
        },

        "&.MuiInputBase-input": {
          "-webkit-text-fill-color": styles.disabled.color,
        },

        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: `${styles.disabled.borderColor} !important`,
        },
      },

      // Without error
      "&:not(.Mui-error)": {
        // Hover
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: styles.normal.hover.borderColor,
        },
        // Focus
        ".Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: styles.normal.focus.borderColor,
          boxShadow: styles.normal.focus.boxShadow,
        },
      },

      // Error
      ".Mui-error": {
        // Hover
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: styles.error.hover.borderColor,
        },
        // Focus
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: styles.error.focus.borderColor,
          boxShadow: styles.error.focus.boxShadow,
        },
      },
    };
  }
);
